import type {ProjectId} from "../../../../metl-models/id/ProjectId.tsx";
import {
    type ProjectTaskBatchesResult,
    type SingleTaskBatchScanResult,
    type TaskSourceBreakdown
} from "../../scan-model/GroupedTaskBatchesScanResult.tsx";
import {calculateBatchOverlaps} from "./BatchOverlapCalculator.tsx";

function aggregateJobTimeAnalyses(projectId: ProjectId, analyses: SingleTaskBatchScanResult[]): ProjectTaskBatchesResult {
    const aggregatedComponentImplementationTimes: Record<number, number> = {};
    const aggregatedComponentImplementationRowCounts: Record<number, number> = {};
    const aggregatedComponentImplementationRuns: Record<number, number> = {};
    const aggregatedJobTimes: Record<string, number> = {};
    const jobsRun: Record<string, number> = {}
    
    let overallStartTime = analyses[0].overallStartTime;
    let overallEndTime = analyses[0].overallEndTime;
    const taskSources: TaskSourceBreakdown = {
        api: 0,
        manual: 0,
        queue: 0,
        scheduled: 0
    }
    let totalTasks = 0;
    let totalBatchDuration = 0;
    let totalOverlapTime = 0;
    let maxOverlap = 0;

    // Aggregate data from all analyses
    analyses.forEach(analysis => {
        // Aggregate component implementation times
        Object.entries(analysis.componentImplementationTimes).forEach(([implId, time]) => {
            const id = parseInt(implId);
            aggregatedComponentImplementationTimes[id] = (aggregatedComponentImplementationTimes[id] || 0) + time;
        });

        Object.entries(analysis.componentImplementationRowCounts).forEach(([implId, count]) => {
            const id = parseInt(implId);
            aggregatedComponentImplementationRowCounts[id] = (aggregatedComponentImplementationRowCounts[id] || 0) + count;
        });

        Object.entries(analysis.componentImplementationRuns).forEach(([implId, count]) => {
            const id = parseInt(implId);
            aggregatedComponentImplementationRuns[id] = (aggregatedComponentImplementationRuns[id] || 0) + count;
        })

        // Aggregate job times
        Object.entries(analysis.jobTimes).forEach(([jobName, time]) => {
            aggregatedJobTimes[jobName] = (aggregatedJobTimes[jobName] || 0) + time;
        });

        analysis.jobsRun.forEach((jobName) => {
            jobsRun[jobName] = (jobsRun[jobName] ?? 0) + 1;
        })

        // Track overall time bounds
        overallStartTime = Math.min(overallStartTime, analysis.overallStartTime);
        overallEndTime = Math.max(overallEndTime, analysis.overallEndTime);

        // Aggregate overlap data
        totalOverlapTime += (analysis.overlaps.meanOverlap * analysis.totalDuration);
        totalBatchDuration += analysis.totalDuration;
        maxOverlap = Math.max(maxOverlap, analysis.overlaps.highestOverlap);

        // Count tasks from the batch info (more accurate than component count)
        totalTasks += Object.keys(analysis.componentImplementationTimes).length;
        taskSources[analysis.batchInfo.source]++
    });

    return {
        projectId,
        scanPeriod: {
            start: new Date(overallStartTime),
            end: new Date(overallEndTime),
            duration: overallEndTime - overallStartTime
        },
        overlaps: {
            highestOverlap: maxOverlap,
            meanOverlap: totalOverlapTime / totalBatchDuration,
            meanOverallOverlap: totalOverlapTime / (overallEndTime - overallStartTime)
        },
        componentImplementationTimes: aggregatedComponentImplementationTimes,
        componentImplementationRowCounts: aggregatedComponentImplementationRowCounts,
        componentImplementationRuns: aggregatedComponentImplementationRuns,
        taskSources,
        totalRunningTime: totalBatchDuration,
        jobTimes: aggregatedJobTimes,
        jobsRun,
        numberOfTaskBatches: totalTasks,
        batchOverlaps: calculateBatchOverlaps(analyses)
    }
}

export function calculateProjectTaskAnalysis(
    projectId: ProjectId,
    individualAnalyses: SingleTaskBatchScanResult[],
): [ProjectTaskBatchesResult, individualAnalyses: SingleTaskBatchScanResult[]] | undefined {
    if (individualAnalyses.length === 0) return undefined;

    // Aggregate the results
    return [aggregateJobTimeAnalyses(projectId, individualAnalyses), individualAnalyses]
}